import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useEffect, useRef, useState } from 'react';
import { FaExternalLinkAlt, FaInfoCircle, FaShoppingCart } from 'react-icons/fa';
import { TiArrowUp } from 'react-icons/ti';
import { useNavigate } from 'react-router-dom';
import AnimatedTitle from './AnimatedTitle';
import Footer from './Footer';
import ScrollDownButtonZeroCoin from './ScrollDownButtonZeroCoin';
import { BackgroundBeams } from './ui/BackgroundBeams';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const ZeroCoin = () => {
  const navigate = useNavigate();
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);

  const sectionRefs = useRef([]);
  const scrollTopRef = useRef(null);

  const handleMoreDetailsClick = () => {
    navigate(`/token/0`);
  };

  const handlePurchaseClick = () => {
    navigate(`/zero-coin/purchase`);
  };

  const handleViewOnChainClick = () => {
    window.open(
      "https://etherscan.io/token/0xa47d4023561527a2ec51b216ab06cc5be34f38bd",
      "_blank"
    );
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            delay: index * 0.2,
            scrollTrigger: {
              trigger: section,
              start: "top 80%",
            },
          }
        );
      }
    });

    if (scrollTopRef.current) {
      gsap.fromTo(
        scrollTopRef.current,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          scrollTrigger: {
            trigger: scrollTopRef.current,
            start: "top bottom",
          },
        }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (
      window.innerHeight + window.scrollY >=
      document.body.offsetHeight - 200
    ) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false); // Reset state to show "Scroll Down" button
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  return (
    <div className="w-screen bg-black text-blue-50 relative overflow-x-hidden">
      {/* Background Effects */}
      <div className="fixed inset-0 z-0">
        <BackgroundBeams className="opacity-30" />
      </div>
      
      {/* Hero Section - Full Viewport Height */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        {/* Hero Video Container */}
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/zero-coin-hero.mp4"
            autoPlay
            loop
            muted
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="zeroCoinHeroSparkles"
              background="transparent"
              minSize={0.4}
              maxSize={1.0}
              particleDensity={30}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Zero-Coin: Empowering Zero-Blockchain"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Digital Fuel · Sovereign Value · Global Utility
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button - Fixed at bottom center */}
      {!scrollDownClicked && (
        <ScrollDownButtonZeroCoin
          targetId="content"
          additionalClasses=""
          onClick={() => setScrollDownClicked(true)}
        />
      )}

      {/* Content Section - Below the fold */}
      <div id="content" className="px-10 py-20 mx-auto max-w-7xl relative z-10">
        {/* Introduction */}
        <motion.div
          className="mb-10 text-center"
          ref={(el) => (sectionRefs.current[0] = el)}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl font-bold bg-gradient-to-r from-white via-cyan-200 to-white bg-clip-text text-transparent">
            Explore our native Coin on the Zero-Blockchain
          </h2>
          <p className="mt-3 text-sm text-gray-300">
            Discover our Coin, creating our Zero-Blockchain, ecosystem and banking products, driving blockchain forward.
          </p>
        </motion.div>

        {/* Zero-Coin Details Section */}
        <motion.div
          id="zero-coin-details"
          className="w-full max-w-4xl mx-auto relative p-8 md:p-12 mb-10 rounded-3xl overflow-hidden"
          ref={(el) => (sectionRefs.current[1] = el)}
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
        >
          {/* Outer glow effect */}
          <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/20 via-white/10 to-cyan-500/20 rounded-3xl blur-xl opacity-60" />
          
          {/* Animated border */}
          <motion.div 
            className="absolute -inset-[2px] rounded-3xl"
            style={{
              background: "linear-gradient(90deg, rgba(34,211,238,0.5), rgba(255,255,255,0.3), rgba(20,184,166,0.5), rgba(255,255,255,0.3), rgba(34,211,238,0.5))",
              backgroundSize: "300% 100%",
            }}
            animate={{
              backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "linear",
            }}
          />
          
          {/* Card background */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-850 to-gray-900 rounded-3xl" />
          
          <div className="relative text-center">
            <h2 className="text-4xl font-bold mb-6 font-zentry bg-gradient-to-r from-white via-cyan-200 to-white bg-clip-text text-transparent drop-shadow-lg">About Zero-Coin</h2>
            <p className="text-gray-200 mb-8 text-lg leading-relaxed max-w-2xl mx-auto">
              Zero-Coin is the native cryptocurrency of Zero-Blockchain.xyz, developed by Zero Bank. It serves as the foundation for all transactions, staking, and governance within our ecosystem.
            </p>
            
            {/* Video with glow effect */}
            <div className="relative inline-block">
              <div className="absolute -inset-2 bg-gradient-to-r from-cyan-500/30 via-white/20 to-cyan-500/30 rounded-2xl blur-md opacity-40" />
              <video
                src="/assets/videos/zero-coin-feature.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="relative mx-auto w-96 h-96 object-contain p-5 border border-white/20 rounded-xl bg-black/50 backdrop-blur-sm"
                draggable="false"
              />
            </div>
            
            <div className="mt-10 text-left grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="p-5 rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/20 backdrop-blur-sm">
                <p className="text-cyan-400 text-sm font-bold uppercase tracking-wider mb-2">Token Standard</p>
                <p className="text-white font-semibold text-lg">ZRC-20 Smart Contract</p>
              </div>
              <div className="p-5 rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/20 backdrop-blur-sm">
                <p className="text-gray-300 text-sm font-bold uppercase tracking-wider mb-2">Deployment</p>
                <p className="text-white font-semibold text-lg">Zero-Blockchain Mainnet</p>
              </div>
              <div className="p-5 rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/20 backdrop-blur-sm md:col-span-2">
                <p className="text-cyan-400 text-sm font-bold uppercase tracking-wider mb-2">Contract Address</p>
                <a
                  href="https://etherscan.io/token/0xa47d4023561527a2ec51b216ab06cc5be34f38bd"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-300 hover:text-cyan-200 underline font-mono text-base break-all transition-colors"
                >
                  0xa47d4023561527a2ec51b216ab06cc5be34f38bd
                </a>
              </div>
              <div className="p-5 rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/20 backdrop-blur-sm md:col-span-2">
                <p className="text-gray-300 text-sm font-bold uppercase tracking-wider mb-2">Description</p>
                <p className="text-white/90 text-base leading-relaxed">Zero-Coin powers all aspects of Zero-Blockchain.xyz, enabling seamless transactions, staking rewards, and decentralised governance.</p>
              </div>
              <div className="p-6 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-teal-500/10 border border-cyan-400/40 md:col-span-2">
                <p className="text-cyan-300 text-sm font-bold uppercase tracking-wider mb-2">Price</p>
                <p className="text-4xl font-bold bg-gradient-to-r from-white via-cyan-200 to-white bg-clip-text text-transparent">£1,000.00</p>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-wrap justify-center mt-10 gap-5">
              <GlassButton
                onClick={handleMoreDetailsClick}
                variant="cyan"
                ariaLabel="More Details about Zero-Coin"
              >
                <FaInfoCircle className="text-sm" />
                More Details
              </GlassButton>
              <GlassButton
                onClick={handlePurchaseClick}
                variant="green"
                ariaLabel="Purchase Zero-Coin"
              >
                <FaShoppingCart className="text-sm" />
                Purchase
              </GlassButton>
              <GlassButton
                onClick={handleViewOnChainClick}
                variant="purple"
                ariaLabel="View Zero-Coin on Etherscan"
              >
                <FaExternalLinkAlt className="text-sm" />
                View On Chain
              </GlassButton>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Back to Top Button */}
      {isBottom && (
        <motion.div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-2 p-3 bg-black/80 backdrop-blur-xl border border-white/10 rounded-full shadow-2xl shadow-cyan-500/20 cursor-pointer hover:border-cyan-500/50 transition-all duration-300 z-50"
          onClick={scrollToTop}
          aria-label="Back to Top"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <TiArrowUp
            className="text-cyan-400"
            size={24}
          />
          <span className="relative z-20 text-sm uppercase text-cyan-400 font-semibold">
            BACK TO TOP
          </span>
        </motion.div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ZeroCoin;
